const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const SAFE_UTM_VALUE = /^[-._~ a-zA-Z0-9]{1,120}$/;
const SAFE_LOOK_VALUE = /^[a-z0-9][a-z0-9-]{0,80}$/;
const SESSION_LOOK_KEY = "elarea-meta-landing-look-v1";

function shuffled(items, random) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const value = random();
    if (!Number.isFinite(value) || value < 0 || value >= 1)
      throw new Error("LANDING_RANDOM_INVALID");
    const swap = Math.floor(value * (index + 1));
    [result[index], result[swap]] = [result[swap], result[index]];
  }
  return result;
}

function unique(items) {
  const ids = new Set();
  return items.filter((item) => {
    if (!item?.id || ids.has(item.id)) return false;
    ids.add(item.id);
    return true;
  });
}

export function selectCandidateComposition(
  { products = [], videos = [], proofs = [] } = {},
  { random = Math.random } = {},
) {
  const eligible = unique(products.filter((item) => item.available !== false));
  const fresh = shuffled(
    eligible.filter((item) => item.pool === "new"),
    random,
  ).slice(0, 4);
  const ids = new Set(fresh.map((item) => item.id));
  const hot = shuffled(
    eligible.filter(
      (item) => item.pool === "bestseller" && !ids.has(item.id),
    ),
    random,
  ).slice(0, 2);
  hot.forEach((item) => ids.add(item.id));
  const fill = shuffled(
    eligible.filter((item) => !ids.has(item.id)),
    random,
  ).slice(0, 6 - ids.size);
  return {
    products: shuffled([...fresh, ...hot, ...fill], random),
    videos: shuffled(unique(videos.filter((item) => item.verified !== false)), random).slice(0, 3),
    proofs: shuffled(unique(proofs.filter((item) => item.verified !== false)), random).slice(0, 5),
  };
}

export function productHandle(item) {
  try {
    const pathname = new URL(item?.productUrl || "", "https://elareanails.com").pathname;
    const match = pathname.match(/^\/products\/([a-z0-9][a-z0-9-]{0,80})\/?$/);
    return match?.[1] || "";
  } catch {
    return "";
  }
}

export function selectHeroCandidate(
  items = [],
  { random = Math.random, preferredHandle = "" } = {},
) {
  const eligible = unique(items.filter(
    (item) => item.available !== false && typeof item.imageSrc === "string" && item.imageSrc.length > 0,
  ));
  if (!eligible.length) return null;
  if (SAFE_LOOK_VALUE.test(preferredHandle)) {
    const preferred = eligible.find((item) => productHandle(item) === preferredHandle);
    if (preferred) return preferred;
  }
  const value = random();
  if (!Number.isFinite(value) || value < 0 || value >= 1)
    throw new Error("LANDING_RANDOM_INVALID");
  return eligible[Math.floor(value * eligible.length)];
}

export function safeAttributionQuery(search = "", selectedLook = "") {
  const input = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const output = new URLSearchParams();
  for (const key of UTM_KEYS) {
    const value = input.get(key);
    if (value !== null && SAFE_UTM_VALUE.test(value)) output.set(key, value);
  }
  const requestedLook = input.get("look") || "";
  const safeLook = SAFE_LOOK_VALUE.test(selectedLook)
    ? selectedLook
    : (SAFE_LOOK_VALUE.test(requestedLook) ? requestedLook : "");
  if (safeLook) output.set("look", safeLook);
  return output.toString();
}

function secureRandom() {
  if (!globalThis.crypto?.getRandomValues)
    throw new Error("LANDING_SECURE_RANDOM_UNAVAILABLE");
  const value = new Uint32Array(1);
  globalThis.crypto.getRandomValues(value);
  return value[0] / 4_294_967_296;
}

async function compositionId(composition) {
  const body = JSON.stringify({
    products: composition.products.map((item) => item.id),
    videos: composition.videos.map((item) => item.id),
    proofs: composition.proofs.map((item) => item.id),
  });
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(body),
  );
  return `lc_${[...new Uint8Array(digest)]
    .slice(0, 8)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

function candidates(root, selector, mapper) {
  return [...root.querySelectorAll(selector)].map((element) => ({
    element,
    id: element.dataset.evidenceId,
    ...mapper(element),
  }));
}

function showSelected(all, selected) {
  const visible = new Set(selected.map((item) => item.id));
  let visiblePosition = 0;
  all.forEach((item) => {
    const show = visible.has(item.id);
    item.element.hidden = !show;
    item.element.setAttribute("aria-hidden", show ? "false" : "true");
    if (show) item.element.dataset.selectedPosition = String(++visiblePosition);
    else delete item.element.dataset.selectedPosition;
  });
}

function forwardAttribution(root, query, identity) {
  const storeHost = root.dataset.storeHost || location.hostname;
  root.querySelectorAll("a[data-product-link], a[data-hero-link]").forEach((anchor) => {
    try {
      const url = new URL(anchor.dataset.baseUrl, location.origin);
      if (url.hostname !== storeHost || !url.pathname.startsWith("/products/")) return;
      const params = new URLSearchParams(query);
      for (const [key, value] of params) url.searchParams.set(key, value);
      url.searchParams.set("lc", identity);
      anchor.href = url.href;
    } catch {}
  });
}

function emit(root, eventName, detail) {
  try {
    globalThis.dataLayer?.push({ event: eventName, ...detail });
    root.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
  } catch {}
}

function initRandomHero(root) {
  const heroCandidates = candidates(root, "[data-hero-candidate]", (element) => ({
    available: element.dataset.available === "true",
    imageSrc: element.dataset.imageSrc,
    imageSrcset: element.dataset.imageSrcset,
    imageSizes: element.dataset.imageSizes,
    imageAlt: element.dataset.imageAlt,
    productUrl: element.dataset.productUrl,
    productTitle: element.dataset.productTitle,
  }));
  const query = new URLSearchParams(location.search);
  const requestedLook = query.get("look") || "";
  let storedLook = "";
  try {
    storedLook = sessionStorage.getItem(SESSION_LOOK_KEY) || "";
  } catch {}
  let preferredHandle = SAFE_LOOK_VALUE.test(requestedLook) ? requestedLook : "";
  let selectionSource = preferredHandle ? "ad_parameter" : "random";
  if (!preferredHandle && SAFE_LOOK_VALUE.test(storedLook)) {
    preferredHandle = storedLook;
    selectionSource = "session";
  }
  const selected = selectHeroCandidate(heroCandidates, { random: secureRandom, preferredHandle });
  const image = root.querySelector("[data-hero-image]");
  const link = root.querySelector("[data-hero-link]");
  if (!selected || !image || !link) return null;
  const handle = productHandle(selected);
  if (preferredHandle && handle !== preferredHandle) selectionSource = "random";
  if (handle) {
    try { sessionStorage.setItem(SESSION_LOOK_KEY, handle); } catch {}
  }
  image.alt = selected.imageAlt || selected.productTitle || "Elaréa Nails";
  if (selected.imageSizes) image.sizes = selected.imageSizes;
  if (selected.imageSrcset) image.srcset = selected.imageSrcset;
  image.src = selected.imageSrc;
  link.dataset.baseUrl = selected.productUrl;
  link.href = selected.productUrl;
  link.setAttribute("aria-label", `${selected.productTitle || image.alt} ansehen`);
  link.hidden = false;
  link.setAttribute("aria-hidden", "false");
  root.dataset.heroProductId = selected.id;
  root.dataset.heroProductHandle = handle;
  root.dataset.heroSelectionSource = selectionSource;
  return selected;
}

export function initLoopingVideos(root) {
  root.querySelectorAll("[data-video-candidate]:not([hidden]) video[data-loop-start]").forEach((video) => {
    const start = Number(video.dataset.loopStart);
    const end = Number(video.dataset.loopEnd);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end <= start)
      return;
    const card = video.closest("[data-video-candidate]");
    const setVideoState = (state) => {
      if (card) card.dataset.videoState = state;
    };
    const loadVideo = () => {
      if (video.dataset.loaded === "true") return;
      setVideoState("loading");
      if (video.dataset.poster) video.poster = video.dataset.poster;
      video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });
      video.dataset.loaded = "true";
      video.preload = "metadata";
      video.addEventListener("loadeddata", () => setVideoState("ready"), { once: true });
      video.addEventListener("error", () => setVideoState("error"), { once: true });
      video.load();
    };
    const seekToStart = () => {
      if (Number.isFinite(video.duration) && start < video.duration)
        video.currentTime = start;
    };
    if (video.readyState >= 1) seekToStart();
    else video.addEventListener("loadedmetadata", seekToStart, { once: true });
    video.addEventListener("timeupdate", () => {
      if (video.currentTime >= end) video.currentTime = start;
    });
    if (!("IntersectionObserver" in globalThis)) {
      loadVideo();
      video.play().catch(() => {});
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadVideo();
          video.play().catch(() => {});
        }
        else video.pause();
      });
    }, { rootMargin: "0px", threshold: 0.25 });
    observer.observe(video);
  });
}

export async function bootLanding(root) {
  const hero = initRandomHero(root);
  const paidParams = new URLSearchParams(location.search);
  const paidLook = paidParams.get("utm_source") === "meta"
    && paidParams.get("utm_medium") === "paid_social"
    && root.dataset.heroSelectionSource === "ad_parameter"
    && Boolean(hero?.productUrl);
  if (paidLook) {
    root.classList.add("elarea-meta-landing--paid-look");
    document.body.classList.add("elarea-meta-landing-paid-look");
    const eyebrow = root.querySelector(".elarea-meta-landing__hero-copy .elarea-meta-landing__eyebrow");
    if (eyebrow) eyebrow.textContent = `${hero.productTitle} · 10 STÜCK · HANDGEFERTIGT`;
    root.querySelectorAll('a.elarea-meta-landing__cta[href^="#elarea-meta-products-"]').forEach((anchor) => {
      anchor.dataset.productLink = "";
      anchor.dataset.baseUrl = hero.productUrl;
      anchor.textContent = `${hero.productTitle} ansehen`;
      anchor.setAttribute("aria-label", `${hero.productTitle} ansehen`);
    });
  }
  const all = {
    products: candidates(root, "[data-product-candidate]", (element) => ({
      pool: element.dataset.pool,
      available: element.dataset.available === "true",
    })),
    videos: candidates(root, "[data-video-candidate]", (element) => ({
      verified: element.dataset.verified === "true",
    })),
    proofs: candidates(root, "[data-proof-candidate]", (element) => ({
      verified: element.dataset.verified === "true",
    })),
  };
  const selected = selectCandidateComposition(all, { random: secureRandom });
  showSelected(all.products, selected.products);
  showSelected(all.videos, selected.videos);
  showSelected(all.proofs, selected.proofs);
  initLoopingVideos(root);
  const identity = await compositionId(selected);
  root.dataset.compositionId = identity;
  root.dataset.rotationState = "randomized";
  const query = safeAttributionQuery(location.search, productHandle(hero));
  forwardAttribution(root, query, identity);
  const detail = {
    composition_id: identity,
    hero_product_id: hero?.id ?? null,
    product_ids: selected.products.map((item) => item.id),
    video_ids: selected.videos.map((item) => item.id),
    proof_ids: selected.proofs.map((item) => item.id),
  };
  emit(root, "elarea_landing_view", detail);
  root.addEventListener("click", (event) => {
    const link = event.target.closest?.("a[data-product-link], a[data-hero-link]");
    if (!link) return;
    emit(root, "elarea_landing_select_product", {
      composition_id: identity,
      hero_product_id: hero?.id ?? null,
      product_id: link.closest("[data-product-candidate]")?.dataset.evidenceId || hero?.id || null,
      selection_source: root.dataset.heroSelectionSource || "unknown",
    });
  });
}

if (typeof document !== "undefined") {
  const start = () => {
    const roots = document.querySelectorAll("[data-elarea-meta-landing]");
    if (!roots.length) return;
    document.body.classList.add("elarea-meta-landing-page");
    roots.forEach((root) => {
      bootLanding(root).catch(() => {
        root.dataset.rotationState = "default_fallback";
      });
    });
  };
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
